/**
 * Safe-Area-Ausrichtung der Szene: pro Slide gepflegt, ueber die Slides hinweg
 * ueberblendet. Das Ergebnis verschiebt `groupRoot` — den Container ALLER
 * Ebenen. Ein NaN hier macht die komplette WebGL-Szene unsichtbar, waehrend die
 * separat positionierten HTML-Overlays weiter erscheinen.
 *
 * Genau das war Jans/Benedikts Fehlerbild vom 04.08.2026 ("Es fehlen saemtliche
 * Bilder und Elemente — nur blauer Hintergrund und die HTML-Boxen"; Benedikt
 * zuvor: "die Bilder laden, werden aber nicht angezeigt" — geladen waren sie,
 * nur eben an einer NaN-Position).
 *
 * ZWEI NaN-Quellen steckten hier, beide hier geschlossen:
 *
 * 1. RUECKFALL PRO OBJEKT statt pro Feld. Vorher:
 *        const options = slide.options || presentationOptions.slideOptions
 *    Template-Slides bringen ALLE ein eigenes `options`-Objekt mit, in dem die
 *    Safe-Area-Felder fehlen. Damit war die globale Vorgabe (die die Werte
 *    hatte) komplett verdeckt, x/y wurden `undefined` und die Interpolation
 *    machte daraus NaN. Einfache Templates ohne eigene Slide-Options waren
 *    unauffaellig — daher "manche Templates gehen, andere nicht".
 *
 * 2. FENSTER DER LAENGE NULL. Liegt der Playhead hinter der letzten Slide,
 *    zeigen "aktuell" und "naechste" auf dasselbe Element; die Differenz ist 0
 *    und der Fortschritt wird 0/0 = NaN.
 */

export interface SafeAreaSlide {
    from: number;
    options?: Record<string, unknown> | null;
}

export interface SafeAreaStop {
    from: number;
    x: number;
    y: number;
}

const X_FELD = "safeAreaXPosition";
const Y_FELD = "safeAreaYPosition";

function zahlOderNull(wert: unknown): number | null {
    return typeof wert === "number" && Number.isFinite(wert) ? wert : null;
}

/**
 * Einen Safe-Area-Wert aufloesen: eigener Slide-Wert, sonst die globale
 * Vorgabe, sonst 0 (= zentriert). Eine unbeabsichtigte Null ist harmlos, ein
 * NaN legt die ganze Szene lahm.
 */
function feld(
    eigene: Record<string, unknown> | null | undefined,
    global: Record<string, unknown> | null | undefined,
    name: string,
): number {
    return zahlOderNull(eigene?.[name]) ?? zahlOderNull(global?.[name]) ?? 0;
}

/** Stuetzstellen der Ausrichtung — eine pro Slide, immer mit gueltigen Zahlen. */
export function buildSafeAreaStops(
    slides: ReadonlyArray<SafeAreaSlide>,
    globalSlideOptions: Record<string, unknown> | null | undefined,
): SafeAreaStop[] {
    return slides.map((slide) => {
        const eigene = slide.options ?? globalSlideOptions;
        return {
            from: slide.from,
            x: feld(eigene, globalSlideOptions, X_FELD),
            y: feld(eigene, globalSlideOptions, Y_FELD),
        };
    });
}

/**
 * Ueberblend-Fortschritt zwischen zwei Stuetzstellen, 0..1 — nie NaN.
 * Ohne Fenster (Laenge 0) gibt es nichts zu ueberblenden: 1 = beim Ziel.
 */
export function safeAreaProgress(
    slideToIndex: number,
    timer: number,
    aktuell: SafeAreaStop,
    naechste: SafeAreaStop,
): number {
    if (slideToIndex === 0) return 0;
    const dauer = naechste.from - aktuell.from;
    if (!(dauer > 0)) return 1;
    const p = (timer - aktuell.from) / dauer;
    if (!Number.isFinite(p)) return 1;
    return Math.min(1, Math.max(0, p));
}
